import React, { useEffect, useMemo, useState } from 'react';

/**
 * TRAFFIC HISTORY (God 2026-09-30): "har din kaun symbol and TF kitna time chala,
 * kitne baje se kitne tak, din me kitni blockage aayi - usi chart par, weekly and
 * monthly". Reads /reports/traffic/history (Mongo traffic_daily, written once a
 * minute by modules/traffic_monitor.py; blocked = entries the 30 ms gate refused).
 *
 * Day view: one row per symbol x TF with a timeline strip 09:00-24:00 IST
 * (green PASS, amber FAIL, red JAM, blank = not measured). Week / month: totals
 * per cell plus one small bar per day showing that day's running share.
 */

const API = 'https://api.jaiswalcapital.com';
const TF_RANK: Record<string, number> = { '1s': 1, '5s': 2, '15s': 3, '1m': 4, '5m': 5, '15m': 6 };
const T0 = 9 * 60, T1 = 24 * 60;                       // strip spans 09:00 -> 24:00 IST
const COL: Record<string, string> = { P: '#10b981', F: '#f59e0b', J: '#ef4444' };

type DayRow = { day: string; symbol: string; tf: string; armed?: boolean; run_min?: number; hold_min?: number;
  jam_min?: number; episodes?: number; blocked?: number; worst_ms?: number; first_run_m?: number;
  last_run_m?: number; first_m?: number; last_m?: number; t?: Record<string, string> };
type Tot = { symbol: string; tf: string; armed?: boolean | null; days: number; run_min: number; hold_min: number;
  jam_min: number; episodes: number; blocked: number; worst_ms: number };

const istDay = (offsetDays = 0) => {
  const d = new Date(Date.now() + 330 * 60000 - offsetDays * 86400000);
  return d.toISOString().slice(0, 10);
};
const hm = (m?: number) => (m == null ? '—' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
const dur = (m?: number) => (!m ? '0m' : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`);
const lag = (v?: number) => (v == null || !v ? '—' : v < 1000 ? `${Math.round(v)} ms` : `${(v / 1000).toFixed(1)} s`);

/** The day's minute map as one CSS gradient: consecutive equal minutes become one stop pair. */
function strip(t?: Record<string, string>): string {
  if (!t) return 'transparent';
  const span = T1 - T0;
  const stops: string[] = [];
  let cur = '', start = T0;
  const push = (end: number) => {
    const c = cur ? COL[cur] : 'transparent';
    stops.push(`${c} ${((start - T0) / span * 100).toFixed(3)}%`, `${c} ${((end - T0) / span * 100).toFixed(3)}%`);
  };
  for (let m = T0; m < T1; m++) {
    const s = t[String(m)] || '';
    if (s !== cur) { push(m); cur = s; start = m; }
  }
  push(T1);
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

const TrafficHistory: React.FC = () => {
  const [range, setRange] = useState<'day' | 'week' | 'month'>('day');
  const [day, setDay] = useState(istDay());
  const [data, setData] = useState<{ days: DayRow[]; totals: Tot[] } | null>(null);
  const [err, setErr] = useState('');

  const frm = range === 'day' ? day : range === 'week' ? istDay(6) : istDay(29);
  const to = range === 'day' ? day : istDay();

  useEffect(() => {
    let dead = false;
    const pull = async () => {
      try {
        const r = await fetch(`${API}/reports/traffic/history?frm=${frm}&to=${to}&timeline=${range === 'day' ? 1 : 0}&t=${Date.now()}`,
          { cache: 'no-store', signal: AbortSignal.timeout(20000) });
        if (!r.ok) throw new Error(r.status === 404 ? 'history API abhi deploy nahi hua (404)' : `HTTP ${r.status}`);
        const d = await r.json();
        if (d?.error) throw new Error(d.error);
        if (!dead) { setData(d); setErr(''); }
      } catch (e: any) { if (!dead) setErr(String(e?.message || e)); }
    };
    pull();
    const iv = setInterval(() => { if (!document.hidden && to === istDay()) pull(); }, 60000);
    return () => { dead = true; clearInterval(iv); };
  }, [frm, to, range]);

  const dates = useMemo(() => {
    const out: string[] = [];
    for (let d = new Date(frm + 'T00:00:00Z'); d <= new Date(to + 'T00:00:00Z'); d = new Date(d.getTime() + 86400000))
      out.push(d.toISOString().slice(0, 10));
    return out;
  }, [frm, to]);
  const byCellDay = useMemo(() => {
    const m: Record<string, Record<string, DayRow>> = {};
    for (const r of data?.days || []) (m[`${r.symbol}|${r.tf}`] ||= {})[r.day] = r;
    return m;
  }, [data]);
  const sorted = <T extends { symbol: string; tf: string }>(xs: T[]) =>
    [...xs].sort((a, b) => a.symbol.localeCompare(b.symbol) || (TF_RANK[a.tf] || 9) - (TF_RANK[b.tf] || 9));
  const sum = (f: keyof Tot) => (data?.totals || []).reduce((a, t) => a + (Number(t[f]) || 0), 0);

  const th: React.CSSProperties = { padding: '3px 6px', color: '#64748b', fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '3px 6px', whiteSpace: 'nowrap' };

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid #1e293b', paddingTop: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '.06em', color: '#e2e8f0' }}>📅 TRAFFIC HISTORY</span>
        {(['day', 'week', 'month'] as const).map(k => (
          <button key={k} onClick={() => setRange(k)}
            style={{ padding: '3px 10px', borderRadius: 5, fontSize: 10, fontWeight: 800, cursor: 'pointer',
                     background: range === k ? 'rgba(245,158,11,.15)' : 'transparent',
                     color: range === k ? '#f59e0b' : '#64748b', border: `1px solid ${range === k ? '#f59e0b55' : '#1e293b'}` }}>
            {k === 'day' ? 'DIN' : k === 'week' ? 'HAFTA (7 din)' : 'MAHINA (30 din)'}
          </button>
        ))}
        {range === 'day' && (
          <input type="date" value={day} max={istDay()} onChange={e => e.target.value && setDay(e.target.value)}
            style={{ background: '#0f172a', color: '#cbd5e1', border: '1px solid #1e293b', borderRadius: 5, fontSize: 11, padding: '2px 6px' }} />
        )}
        {data && (
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {frm}{frm !== to ? ` → ${to}` : ''} · run <b style={{ color: '#10b981' }}>{dur(sum('run_min'))}</b> ·
            hold <b style={{ color: '#f59e0b' }}>{dur(sum('hold_min'))}</b> · hold episodes <b>{sum('episodes')}</b> ·
            entries blocked <b style={{ color: '#ef4444' }}>{sum('blocked')}</b>
          </span>
        )}
        {err && <span style={{ fontSize: 11, color: '#f87171' }}>⚠ {err}</span>}
      </div>

      {data && data.totals.length === 0 && (
        <div style={{ fontSize: 11, color: '#64748b' }}>Is range me koi record nahi — recording 30-Sep se shuru hui; market ke waqt har minute likhi jaati hai.</div>
      )}

      {data && data.totals.length > 0 && range === 'day' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>SYMBOL × TF</th><th style={th}>CHALA (run)</th><th style={th}>KAB SE – KAB TAK</th>
                <th style={th}>RUKA (hold)</th><th style={th}>BAAR RUKA</th><th style={th}>ENTRY RUKI</th><th style={th}>WORST</th>
                <th style={{ ...th, width: '38%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {['09', '12', '15', '18', '21', '24'].map(h => <span key={h}>{h}:00</span>)}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted(data.days).map(r => (
                <tr key={`${r.symbol}|${r.tf}`} style={{ borderTop: '1px solid #1e293b' }}>
                  <td style={{ ...td, color: '#e2e8f0', fontWeight: 700 }}>{r.symbol} {r.tf}{r.armed ? '' : <span style={{ color: '#475569' }}> · dry</span>}</td>
                  <td style={{ ...td, color: '#10b981' }}>{dur(r.run_min)}</td>
                  <td style={{ ...td, color: '#cbd5e1' }}>{r.first_run_m != null ? `${hm(r.first_run_m)} – ${hm(r.last_run_m)}` : 'kabhi nahi chala'}</td>
                  <td style={{ ...td, color: '#f59e0b' }}>{dur(r.hold_min)}{r.jam_min ? <span style={{ color: '#ef4444' }}> (JAM {dur(r.jam_min)})</span> : ''}</td>
                  <td style={{ ...td, color: '#cbd5e1' }}>{r.episodes || 0}</td>
                  <td style={{ ...td, color: r.blocked ? '#ef4444' : '#64748b' }}>{r.blocked || 0}</td>
                  <td style={{ ...td, color: '#94a3b8' }}>{lag(r.worst_ms)}</td>
                  <td style={td}>
                    <div title={`measured ${hm(r.first_m)} – ${hm(r.last_m)}`}
                      style={{ height: 12, borderRadius: 3, background: strip(r.t), border: '1px solid #1e293b' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totals.length > 0 && range !== 'day' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>SYMBOL × TF</th><th style={th}>DIN</th><th style={th}>CHALA (run)</th><th style={th}>RUKA (hold)</th>
                <th style={th}>RUN %</th><th style={th}>BAAR RUKA</th><th style={th}>ENTRY RUKI</th><th style={th}>WORST</th>
                <th style={th}>HAR DIN KA RUN % ({dates[0]?.slice(5)} → {dates[dates.length - 1]?.slice(5)})</th>
              </tr>
            </thead>
            <tbody>
              {sorted(data.totals).map(t => {
                const k = `${t.symbol}|${t.tf}`;
                const meas = t.run_min + t.hold_min;
                return (
                  <tr key={k} style={{ borderTop: '1px solid #1e293b' }}>
                    <td style={{ ...td, color: '#e2e8f0', fontWeight: 700 }}>{t.symbol} {t.tf}{t.armed === false ? <span style={{ color: '#475569' }}> · dry</span> : ''}</td>
                    <td style={{ ...td, color: '#94a3b8' }}>{t.days}</td>
                    <td style={{ ...td, color: '#10b981' }}>{dur(t.run_min)}</td>
                    <td style={{ ...td, color: '#f59e0b' }}>{dur(t.hold_min)}</td>
                    <td style={{ ...td, color: '#cbd5e1' }}>{meas ? `${Math.round(100 * t.run_min / meas)}%` : '—'}</td>
                    <td style={{ ...td, color: '#cbd5e1' }}>{t.episodes}</td>
                    <td style={{ ...td, color: t.blocked ? '#ef4444' : '#64748b' }}>{t.blocked}</td>
                    <td style={{ ...td, color: '#94a3b8' }}>{lag(t.worst_ms)}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 1, alignItems: 'flex-end', height: 16 }}>
                        {dates.map(d => {
                          const r = byCellDay[k]?.[d];
                          const m = (r?.run_min || 0) + (r?.hold_min || 0);
                          const pct = m ? (r!.run_min || 0) / m : null;
                          return (
                            <div key={d} title={r ? `${d}: run ${dur(r.run_min)} · hold ${dur(r.hold_min)} · blocked ${r.blocked || 0}` : `${d}: no record`}
                              style={{ flex: 1, minWidth: 3, height: pct == null ? 2 : 4 + 12 * pct,
                                       background: pct == null ? '#1e293b' : pct >= 0.5 ? '#10b981' : pct > 0 ? '#f59e0b' : '#ef4444',
                                       borderRadius: 1 }} />
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrafficHistory;
